import type { ButtonHTMLAttributes, JSX, ReactNode } from 'react';

import CircularProgress from '@mui/material/CircularProgress';
import styled, { css } from 'styled-components';

const Button = styled.button`
  cursor: pointer;
`;

interface FormButtonProps {
  isPrimary?: boolean;
  isSecondary?: boolean;
  isCancel?: boolean;
}

const FormButton = styled(Button)<FormButtonProps>`
  font-weight: bold;
  border: none;
  background-color: transparent;
  color: var(--grey-2);
  font-size: 1em;
  padding: 15px 60px;

  &:disabled {
    color: var(--grey-1);
    background-color: var(--grey-4);
  }

  ${({ isPrimary }) =>
    isPrimary &&
    css`
      background: var(--cargill-green);
      color: var(--white);
    `}

  ${({ isSecondary }) =>
    isSecondary &&
    css`
      background: var(--grey-3);
      color: var(--white);
    `}

  ${({ isCancel }) =>
    isCancel &&
    css`
      background: var(--grey-1);
      color: var(--grey-4);
    `}

  @media only screen and (max-width: 700px) {
    padding: 15px 25px;
  }
`;

/* ----------------------------------
   Upload button
---------------------------------- */

const UploadButtonWrapper = styled(Button).attrs(() => ({
  type: 'button'
}))`
  position: relative;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  width: 100%;
  color: var(--white);
  padding: 10px;
  background-color: var(--cargill-green);
  border: none;
  font-weight: bold;
  margin: 8px 0;

  .MuiCircularProgress-colorPrimary {
    color: white;
  }
`;

const PlusImage = styled.img`
  margin-right: 10px;
`;

const SignButtonWrapper = styled(Button)`
  position: relative;
  display: flex;
  justify-content: space-around;
  align-items: center;
  width: 20%;
  color: var(--white);
  padding: 10px;
  background-color: var(--action-trigger);
  border: none;
  font-weight: bold;
  margin: 8px 0;
  font-size: 1em;

  &:disabled {
    color: var(--grey-1);
    background-color: var(--grey-4);
  }

  .MuiCircularProgress-colorPrimary {
    color: white;
  }

  @media only screen and (max-width: 700px) {
    width: 40%;
  }
`;

const Loader = styled(CircularProgress)`
  position: absolute;
  top: 25%;
  right: 10px;
`;

/* ----------------------------------
   Component props
---------------------------------- */

interface ButtonWithLoaderProps
  extends ButtonHTMLAttributes<HTMLButtonElement> {
  children: ReactNode;
  loading?: boolean;
}

/* ----------------------------------
   UploadButton
---------------------------------- */

function UploadButton({
  children,
  loading,
  ...props
}: ButtonWithLoaderProps): JSX.Element {
  return (
    <UploadButtonWrapper {...props} type="button">
      <PlusImage src="/food/onboarding/assets/Plus.svg" alt="" />
      {children}
      {loading && <Loader size="20px" />}
    </UploadButtonWrapper>
  );
}
/* ----------------------------------
   SignButton
---------------------------------- */

function SignButton({
  children,
  loading,
  ...props
}: ButtonWithLoaderProps): JSX.Element {
  return (
    <SignButtonWrapper {...props}>
      {children}
      {loading && <Loader size="20px" />}
    </SignButtonWrapper>
  );
}

export { Button, FormButton, SignButton, UploadButton };